import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchNearExpiryReport,
  exportNearExpiryReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchInventoryCategories, fetchAllInventorySubcategories } from 'store/slices/commonSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { AlertTriangle, Calendar, Package, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import FormField from 'components/common/FormField';
import CategorySubcategorySelect from 'components/common/CategorySubcategorySelect';

const ExpiredItemsPage = () => {
  const dispatch = useDispatch();

  const {
    nearExpiryReport = {},
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = nearExpiryReport?.data?.summary || {};
  const results = nearExpiryReport?.data?.results || [];
  const pagination = nearExpiryReport?.pagination || {};

  // Get categories and subcategories from Redux state
  const categories = useSelector((state) => state.common?.inventoryCategories.items || []);

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    status: 'all',
    category: 'all',
    sub_category: 'all',
    days_gte: '',
    days_lte: '',
    near_days: '30', // Default value for near_days
  });

  const subcategories = useSelector((state) => state.common?.allInventorySubcategories.items || []);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);



  // Calculate total pages from server response
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' and default values)
  const hasActiveAdvancedFilters = Object.entries(advancedFilters).some(([key, value]) =>
    key !== 'near_days' && value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'expired_near_expiry',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
      ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
      ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
      ...(advancedFilters.days_gte && { days__gte: advancedFilters.days_gte }),
      ...(advancedFilters.days_lte && { days__lte: advancedFilters.days_lte }),
      ...(advancedFilters.near_days !== '30' && { near_days: advancedFilters.near_days }),
    };

    dispatch(fetchNearExpiryReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(fetchInventoryCategories());
    dispatch(fetchAllInventorySubcategories());
  }, [dispatch]);


  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      status: 'all',
      category: 'all',
      sub_category: 'all',
      days_gte: '',
      days_lte: '',
      near_days: '30', // Reset to default value
    });
  };

  const columns = [
    { accessorKey: 'Category', header: 'Category' },
    { accessorKey: 'Sub Category', header: 'Sub Category' },
    { accessorKey: 'Brand/Model', header: 'Brand / Model' },
    { accessorKey: 'Serial No', header: 'Serial No' },
    { accessorKey: 'Expiry Date', header: 'Expiry Date' },
    {
      accessorKey: 'Status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.Status;
        const variant = status === 'Expired' ? 'destructive' : 'secondary';
        return <Badge variant={variant}>{status}</Badge>;
      },
    },
    {
      accessorKey: 'Days',
      header: 'Days to/from Expiry',
      cell: ({ row }) => {
        const days = row.original.Days;
        const isExpired = row.original.Status === 'Expired';
        return (
          <span className={isExpired ? 'text-destructive font-medium' : 'text-orange-600'}>
            {days}
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Near Expiry & Expired Items Report"
        description="Monitor items approaching or past their expiry date"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

              <FormField
                label="Status"
                name="status"
                type="select"
                value={advancedFilters.status}
                onChange={(e) =>
                  handleAdvancedFilterChange('status', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'Expired', label: 'Expired' },
                  { value: 'Near Expiry', label: 'Near Expiry' },
                  { value: 'Valid', label: 'Valid' },
                ]}
              />

              {/* <FormField
                label="Category"
                name="category"
                type="select"
                value={advancedFilters.category}
                onChange={(e) =>
                  handleAdvancedFilterChange('category', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  ...(Array.isArray(categories)
                    ? categories.map((category) => ({
                      value: String(category.id),
                      label: category.name,
                    }))
                    : []),
                ]}
              />

              <FormField
                label="Sub Category"
                name="sub_category"
                type="select"
                value={advancedFilters.sub_category}
                onChange={(e) =>
                  handleAdvancedFilterChange('sub_category', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  ...(Array.isArray(subcategories)
                    ? subcategories.map((subcategory) => ({
                      value: String(subcategory.id),
                      label: subcategory.name,
                    }))
                    : []),
                ]}
              /> */}


                            <CategorySubcategorySelect
  category={advancedFilters.category}
  subCategory={advancedFilters.sub_category}
  onCategoryChange={(value) => {
    setAdvancedFilters((prev) => ({
      ...prev,
      category: value,
      sub_category: "all",
    }));
  }}
  onSubCategoryChange={(value) =>
    setAdvancedFilters((prev) => ({
      ...prev,
      sub_category: value,
    }))
  }
/>


              <div className="space-y-2">
                <Label>Days to Expiry Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.days_gte}
                    onChange={(e) => handleAdvancedFilterChange('days_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.days_lte}
                    onChange={(e) => handleAdvancedFilterChange('days_lte', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="near_days">Near Expiry Threshold (days)</Label>
                <Input
                  type="number"
                  placeholder="30"
                  value={advancedFilters.near_days}
                  onChange={(e) => handleAdvancedFilterChange('near_days', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary['Total Items'] ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expired</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary['Expired'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Near Expiry</CardTitle>
            <Calendar className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {summary['Near Expiry'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Threshold</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary['Near Expiry Threshold (days)'] ?? 30} days
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading expiry data...</span>
        </div>
      ) : ( */}
      <DataTable
        data={results} // Use the paginated results from the server
        columns={columns}
        totalCount={pagination.count}
        searchPlaceholder="Search by category, sub-category, brand, or serial..."
        onSearch={setSearchTerm}
        showSearch={true}
        pageIndex={page - 1} // Convert to 0-based index for DataTable
        pageSize={pageSize}
        pageCount={totalPages} // Use total pages from server response
        onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        isLoading={isLoading}
        onExport={() =>
          dispatch(exportNearExpiryReport({
            filters: {
              page,
              page_size: pageSize,
              search: searchTerm,
              report_type: 'expired_near_expiry',
              ...dateFilters,
              // Include advanced filters in export
              ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
              ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
              ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
              ...(advancedFilters.days_gte && { days__gte: advancedFilters.days_gte }),
              ...(advancedFilters.days_lte && { days__lte: advancedFilters.days_lte }),
              ...(advancedFilters.near_days !== '30' && { near_days: advancedFilters.near_days }),
            }
          }))
        }
        showExport={true}
        showImport={false}
      />
      {/* )} */}
    </div>
  );
};

export default ExpiredItemsPage;